// src/FileGenerator/AdminFileGenerator/generatePDFReport.js
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Universal PDF generator for all admin reports
 *
 * @param {Array} data - The table data array (each element = row object)
 * @param {Object} options - Report options
 * @param {string} options.title - Report title (e.g., "Offer Letter Report")
 * @param {string} [options.department] - Department name or "All Departments"
 * @param {string} [options.month] - Optional month name (e.g., "January" or "All Months")
 * @param {string} [options.year] - Optional year (e.g., "2025" or "All Years")
 * @param {string} [options.reportDate] - Date of generation (defaults to today)
 * @param {Array<string>} [options.columns] - Optional custom column headers
 * @param {function} [options.rowMapper] - Optional mapper function to transform rows
 */

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date)
    .toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    })
    .replace(",", "")
    .replace(/ /g, "-");
};
const generatePDFReport = (
  
  data = [],
  {
    title = "Report",
    department = "All Departments",
    month = "",
    year = "",
    reportDate = formatDate(new Date()),
    columns = null,
    rowMapper = null,
  } = {}
) => {
  try {
    const doc = new jsPDF();
    const startY = 20;

    // --- Header Title ---
    doc.setFontSize(18);
    doc.text(title, 14, startY);

    // --- Horizontal line ---
    doc.setLineWidth(0.5);
    doc.setDrawColor(180, 180, 180);
    doc.line(14, startY + 5, 200, startY + 5);

    // --- Report Meta Info ---
    let infoY = startY + 15;
    doc.setFontSize(11);
    doc.text(`Department: ${department || "All Departments"}`, 14, infoY);
    infoY += 6;

    if (month) {
      doc.text(`Month: ${month}`, 14, infoY);
      infoY += 6;
    }

    if (year) {
      doc.text(`Year: ${year}`, 14, infoY);
      infoY += 6;
    }

    doc.text(`Report Generated: ${reportDate}`, 14, infoY);
    infoY += 10;

    // --- Table Data ---
    if (!data || data.length === 0) {
      doc.text("No data available to display.", 14, infoY);
      doc.save(`${title.replace(/\s+/g, "_")}_${reportDate}.pdf`);
      return;
    }

    // --- Auto generate columns and rows if not given ---
    const tableHeaders = [columns || Object.keys(data[0])];
    const tableRows = data.map((item) =>
      rowMapper
        ? rowMapper(item)
        : (columns || Object.keys(item)).map((key) => String(item[key] ?? "-"))
    );

    doc.autoTable({
  startY: infoY,
  head: tableHeaders,
  body: tableRows,
  styles: {
    fontSize: 8,
    cellPadding: 3,
    halign: "center",  // Center ALL cells
    valign: "middle",
  },
  headStyles: {
    fillColor: [128, 128, 128],
    textColor: 255,
    fontStyle: "bold",
    halign: "center",  // Center header text
    valign: "middle",
  },
  bodyStyles: {
    halign: "center", // Center data cells
    valign: "middle",
  },
  alternateRowStyles: {
    fillColor: [250, 250, 250],
    halign: "center", // Center alternate rows too
  },
  margin: { top: 15, left: 14, right: 14 },
});

    // --- Save file ---
    const safeTitle = title.replace(/\s+/g, "_");
    doc.save(`${safeTitle}_${reportDate}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

export default generatePDFReport;
